import React, { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Container, Paper, Tab, Tabs, Typography } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/styles';
import {
  createBrand,
  getLogoApp,
  getLogoByPath,
  getLogoLeftByPath,
  updateBrand,
} from '../../../utils/api/brand_api';
import DettagliApp from './DettagliApp';
import DettagliWebApp from './DettagliWebApp';
import SpinnerComponent from '../../../components/SpinnerComponent';
import TwoActionsAlertDialog from '../../../components/TwoActionsAlertDialog';
import { reportApiError, reportError } from '../../../utils/reportApiError';

/** Reported by both web logo uploads; the rules beside them promise the same. */
const FORMATO_LOGO_NON_VALIDO =
  'È possibile caricare solo immagini in formato JPEG o PNG di dimensione inferiore a 150KB.';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    textAlign: 'center',
    color: 'black',
    paddingTop: '26px',
    paddingLeft: '4px',
    paddingRight: '4px',
    minHeight: '86vh',
  },
  tabsPaper: {
    boxShadow: theme.shadows[6],
  },
  tabsRoot: {
    borderBottom: `2px solid ${theme.palette.divider}`,
  },
  tabsIndicator: {
    backgroundColor: theme.palette.primary.main,
  },
  tabSelected: {
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  text: {
    margin: '8px 0px 32px 0',
  },
}));

export default function ModificaTemplate(props) {
  const theme = useTheme();
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();

  const isEditMode = !!location.state;

  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [templateData, setTemplateData] = useState({
    nome: isEditMode ? location.state.brandName : '',
    path: isEditMode ? location.state.path : '',
    logo: '',
    logoLeft: '',
    primaryColor: isEditMode
      ? location.state.primaryColor
      : theme.palette.primary.main,
    secondaryColor: isEditMode
      ? location.state.secondaryColor
      : theme.palette.secondary.main,
    textColor: isEditMode
      ? location.state.textColor
      : theme.palette.text.primary,
    primaryColorApp:
      isEditMode && location.state.primaryColorApp
        ? location.state.primaryColorApp
        : '#FF4600',
    secondaryColorApp:
      isEditMode && location.state.secondaryColorApp
        ? location.state.secondaryColorApp
        : '#FD7800',
    tertiaryColorApp:
      isEditMode && location.state.tertiaryColorApp
        ? location.state.tertiaryColorApp
        : '#6FC3DC',
    logoApp: '',
  });
  const [brandName, setBrandName] = useState(templateData.nome);
  const [loghiTemplate, setLoghiTemplate] = useState([templateData.logo]);
  const [logoLeftUrl, setLogoLeftUrl] = useState('');
  const [logoAppUrl, setLogoAppUrl] = useState('');
  const [changesNotSaved, setChangesNotSaved] = useState(false);
  const [selectedColor, setSelectedColor] = useState({
    primary: templateData.primaryColor,
    secondary: templateData.secondaryColor,
    text: templateData.textColor,
    appPrimary: templateData.primaryColorApp,
    appSecondary: templateData.secondaryColorApp,
    appTertiary: templateData.tertiaryColorApp,
  });
  const [twoActionAlert, setTwoActionAlert] = useState({
    isVisible: false,
    title: '',
    message: '',
  });

  useEffect(() => {
    let isMounted = true;

    // Each logo is read on its own: a brand can legitimately have one kind and
    // not another, so a missing logo must not stop the reads that follow it.
    const readLogo = async (read, apply) => {
      try {
        const response = await read();
        const blob = await response.blob();
        if (!isMounted) return;
        apply(blob, URL.createObjectURL(blob));
      } catch (error) {
        // A 404 is the brand having no logo of this kind yet, not a failure.
        if (isMounted && error.status !== 404) {
          reportApiError(error);
        }
      }
    };

    if (location.state?.path) {
      setLoading(true);

      const fetchLogos = async () => {
        await readLogo(
          () => getLogoByPath(location.state.path),
          (blob, url) => {
            setLoghiTemplate([url]);
            setTemplateData((prev) => ({ ...prev, logo: blob }));
          },
        );

        await readLogo(
          () => getLogoLeftByPath(location.state.path),
          (blob, url) => {
            setLogoLeftUrl(url);
            setTemplateData((prev) => ({ ...prev, logoLeft: blob }));
          },
        );

        await readLogo(
          () => getLogoApp(location.state.id),
          (blob, url) => {
            setLogoAppUrl(url);
            setTemplateData((prev) => ({ ...prev, logoApp: blob }));
          },
        );

        if (isMounted) setLoading(false);
      };

      fetchLogos();
    }

    return () => {
      isMounted = false;
    };
  }, [location.state?.path, location.state?.id]);

  const handleTabChange = (_event, newValue) => {
    setTabValue(newValue);
  };

  const handleBrandNameChange = (e) => {
    const inputValue = e.target.value;
    setBrandName(inputValue);
    setChangesNotSaved(true);
    if (!isEditMode) {
      const restrictedInput = inputValue
        .replace(/\s+/g, '-')
        .replace(/[^a-zA-Z0-9-]/g, '')
        .toLowerCase();
      setTemplateData((prev) => ({ ...prev, path: restrictedInput }));
    }
  };

  const handlePathChange = (e) => {
    const inputValue = e.target.value;
    const restrictedInput = inputValue
      .replace(/\s+/g, '-')
      .replace(/[^a-zA-Z0-9-]/g, '')
      .toLowerCase();
    setTemplateData((prev) => ({ ...prev, path: restrictedInput }));
    setChangesNotSaved(true);
  };

  const handleColorChange = (colorType, color) => {
    if (color?.hex) {
      setSelectedColor((prev) => ({ ...prev, [colorType]: color.hex }));
      setChangesNotSaved(true);
    }
  };

  const uploadImage = (file, event) => {
    if (
      (file.type === 'image/png' || file.type === 'image/jpeg') &&
      Math.floor(file.size / 1000) < 150
    ) {
      const blob = new Blob([file], { type: 'image/png' });
      const url = URL.createObjectURL(blob);
      const loghiTemplateToUse = [...loghiTemplate];
      loghiTemplateToUse[0] = url;
      setLoghiTemplate(loghiTemplateToUse);
      setTemplateData((prev) => ({
        ...prev,
        logo: file,
      }));
      setChangesNotSaved(true);
    } else {
      reportError(FORMATO_LOGO_NON_VALIDO, 'Attenzione');
    }
  };

  const uploadLogoLeft = (file) => {
    if (!file) {
      reportError('Nessun file selezionato.');
      return;
    }
    if (
      (file.type === 'image/png' || file.type === 'image/jpeg') &&
      Math.floor(file.size / 1000) < 150
    ) {
      // The `File` is what gets sent: the backend stores the bytes as they
      // arrive and serves them back under the content type and filename the
      // upload carried, so both have to be the file's own.
      setTemplateData((prev) => ({
        ...prev,
        logoLeft: file,
      }));
      setLogoLeftUrl(URL.createObjectURL(file));
      setChangesNotSaved(true);
    } else {
      reportError(FORMATO_LOGO_NON_VALIDO, 'Attenzione');
    }
  };

  const uploadLogoApp = (file) => {
    if (file.type === 'image/svg+xml') {
      const blob = new Blob([file], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      setTemplateData((prev) => ({
        ...prev,
        logoApp: blob,
      }));
      setLogoAppUrl(url);
      setChangesNotSaved(true);
    } else {
      reportError(
        'È possibile caricare solo immagini in formato SVG.',
        'Attenzione',
      );
    }
  };

  /**
   * Creates the brand from what is currently on the two tabs. Both create
   * paths land here - straight from the save button, and from the confirmation
   * of the missing-app-icon warning - so they send the same fields, word a
   * failure the same way and leave the user in the same place.
   */
  const createBrandWithCurrentData = () => {
    setLoading(true);
    const formData = new FormData();
    formData.append('nome', brandName);
    formData.append('path', templateData.path);
    formData.append('primaryColor', selectedColor.primary);
    formData.append('secondaryColor', selectedColor.secondary);
    formData.append('textColor', selectedColor.text);
    formData.append('primaryColorApp', selectedColor.appPrimary);
    formData.append('secondaryColorApp', selectedColor.appSecondary);
    formData.append('tertiaryColorApp', selectedColor.appTertiary);
    formData.append('logo', templateData.logo);
    formData.append('loghi', []);
    if (typeof templateData.logoLeft === 'object')
      formData.append('logoLeft', templateData.logoLeft);
    if (templateData.logoApp.type === 'image/svg+xml')
      formData.append('logoApp', templateData.logoApp);

    return createBrand(formData)
      .then((_response) => {
        setChangesNotSaved(false);
        history.replace('/amministrazione/brand');
      })
      .catch((error) => {
        if (error.status === 409) {
          reportApiError(error, 'Attenzione');
        } else {
          reportError('Attenzione, creazione del brand fallita.');
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const createOrUpdateBrand = () => {
    if (isEditMode) {
      if (!brandName.trim() || !templateData.logo || !templateData.logoLeft) {
        reportError(
          'Occorre inserire il nome del brand ed entrambi i loghi per procedere.',
          'Attenzione',
        );
        return;
      }
      const formData = new FormData();
      formData.append('id', location.state.id);
      formData.append('name', brandName);
      formData.append('primaryColor', selectedColor.primary);
      formData.append('secondaryColor', selectedColor.secondary);
      formData.append('textColor', selectedColor.text);
      formData.append('primaryColorApp', selectedColor.appPrimary);
      formData.append('secondaryColorApp', selectedColor.appSecondary);
      formData.append('tertiaryColorApp', selectedColor.appTertiary);
      if (typeof templateData.logo === 'object') {
        formData.append('logo', templateData.logo);
      }
      if (typeof templateData.logoLeft === 'object')
        formData.append('logoLeft', templateData.logoLeft);

      if (templateData.logoApp.type === 'image/svg+xml')
        formData.append('logoApp', templateData.logoApp);

      updateBrand(formData)
        .then((_response) => {
          console.log('Brand aggiornato con successo!');
          setChangesNotSaved(false);
          history.goBack();
        })
        .catch((error) => reportApiError(error, 'Attenzione'));
    } else {
      if (
        !brandName.trim() ||
        !templateData.path.trim() ||
        !templateData.logo ||
        !templateData.logoLeft
      ) {
        reportError(
          'Per poter proseguire con la creazione, occorre inserire tutti i dati relativi al brand lato web app (nome, path, palette di colori e loghi).',
          'Attenzione',
        );
        return;
      }

      // The app palette is never empty: it is seeded from the brand's app
      // colours and falls back to the defaults, so the logo is the only part
      // of the app side a new brand can be missing.
      if (!templateData.logoApp) {
        setTwoActionAlert({
          isVisible: true,
          title: 'Attenzione',
          message:
            "Non è stato impostato il logo da utilizzare sull'applicazione. Si desidera proseguire con la creazione del brand? Sarà comunque possibile aggiungerlo in fase di modifica.",
        });
        return;
      }

      createBrandWithCurrentData();
    }
  };

  const handleBackClick = () => {
    if (changesNotSaved) {
      const confirmLeave = window.confirm(
        'Hai delle modifiche non salvate. Sei sicuro di voler uscire?',
      );
      if (confirmLeave) {
        history.goBack();
      }
    } else {
      history.goBack();
    }
  };

  const closeTwoActionsAlert = () =>
    setTwoActionAlert({
      isVisible: false,
      title: '',
      message: '',
    });

  const confirmBrandCreation = () => {
    closeTwoActionsAlert();
    createBrandWithCurrentData();
  };

  return (
    <Container maxWidth={false} className={classes.root}>
      {loading ? (
        <SpinnerComponent size={24} />
      ) : (
        <div>
          <Typography variant="h4" className={classes.text}>
            {isEditMode
              ? `Dettagli del brand ${templateData.nome}`
              : 'Nuovo brand'}
          </Typography>
          <Paper className={classes.tabsPaper}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              classes={{
                indicator: classes.tabsIndicator,
                root: classes.tabsRoot,
              }}
            >
              <Tab
                label="Dettagli web app"
                value={0}
                classes={{
                  selected: classes.tabSelected,
                }}
              />
              <Tab
                label="Dettagli app"
                value={1}
                classes={{
                  selected: classes.tabSelected,
                }}
              />
            </Tabs>
            <div hidden={tabValue !== 0}>
              {tabValue === 0 && (
                <DettagliWebApp
                  isEditMode={isEditMode}
                  brandName={brandName}
                  handleBrandNameChange={handleBrandNameChange}
                  templateData={templateData}
                  handlePathChange={handlePathChange}
                  loghiTemplate={loghiTemplate}
                  uploadImage={uploadImage}
                  logoLeftUrl={logoLeftUrl}
                  uploadLogoLeft={uploadLogoLeft}
                  selectedColor={selectedColor}
                  handleColorChange={handleColorChange}
                  handleBackClick={handleBackClick}
                  createOrUpdateBrand={createOrUpdateBrand}
                />
              )}
            </div>
            <div hidden={tabValue !== 1}>
              {tabValue === 1 && (
                <DettagliApp
                  logoAppUrl={logoAppUrl}
                  uploadLogoApp={uploadLogoApp}
                  templateData={templateData}
                  selectedColor={selectedColor}
                  handleColorChange={handleColorChange}
                  handleBackClick={handleBackClick}
                  createOrUpdateBrand={createOrUpdateBrand}
                />
              )}
            </div>
          </Paper>
        </div>
      )}
      <TwoActionsAlertDialog
        visible={twoActionAlert.isVisible}
        title={twoActionAlert.title}
        message={twoActionAlert.message}
        onIndietroClicked={closeTwoActionsAlert}
        onOkClicked={confirmBrandCreation}
      />
    </Container>
  );
}
